import { SELECTABLE_YEAR_FROM } from '@/consts/UserConst';
import {
  SELECTABLE_MONTH_FROM,
  SELECTABLE_MONTH_TO,
  SELECTABLE_DAY_FROM,
  SELECTABLE_DAY_TO,
} from '@/consts/DateConst';
import type { MonthRange } from '@/types/commons/DateType';

const SPECIAL_MONTH_END_DAYS: number[] = [29, 30, 31];
const LAST_DAY_OF_MONTH: number = 31;

/**
 * 日付をフォーマットする
 * @param date string または Date型の日付
 * @param format string フォーマット デフォルトは'YYYY/MM/DD HH:mm:ss'
 * @return string フォーマットされた日付またはエラーメッセージ
 */
export const formatDate = (date: string | Date, format: string = 'YYYY/MM/DD HH:mm:ss'): string => {
  const { $dayjs } = useNuxtApp();
  // Date型の場合はISO文字列に変換
  const dateStr = date instanceof Date ? date.toISOString() : date;
  const parsedDate = $dayjs(dateStr);
  if (!parsedDate.isValid()) {
    return 'Invalid date format';
  }
  return parsedDate.locale('ja').format(format);
};

const JST_FORMATTER = new Intl.DateTimeFormat('ja-JP', {
  timeZone: 'Asia/Tokyo',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  weekday: 'short',
  hour: 'numeric',
  minute: '2-digit',
  hourCycle: 'h23',
});

/**
 * formatDateTimeJst
 * @description 日時を JST 固定でフォーマットする。実行環境の TZ に依存しないため、
 *              SSR(UTC) とブラウザ(JST) で表示が揺れない。
 * @param isoString ISO 8601 形式の日時（オフセット必須。例: '2026-08-20T23:50:00+09:00'）
 * @return string フォーマットされた日時またはエラーメッセージ（例: 2026年8月20日(木) 23時50分）
 */
export const formatDateTimeJst = (isoString: string): string => {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return 'Invalid date format';
  }

  const parts = Object.fromEntries(
    JST_FORMATTER.formatToParts(date).map((part) => [part.type, part.value])
  );
  return `${parts.year}年${parts.month}月${parts.day}日(${parts.weekday}) ${parts.hour}時${parts.minute}分`;
};

/**
 * formatSlashDate
 * @description Date を `YYYY/M/D` 形式にフォーマットする。
 *              `Date.prototype.toLocaleDateString()` は**ロケール未指定だとブラウザの言語設定で
 *              書式が変わる**（日本語 `2026/9/2` / 韓国語 `2026. 9. 2.` / 英語(米) `9/2/2026`）。
 *              お届け日のようにそのまま API へ送る値では日付として解釈されず注文が失敗するため、
 *              言語設定に依存しないこの関数で整形する。出力は日本語ブラウザでの従来表示と同じ。
 * @param date Date
 * @return string フォーマットされた日付（例: 2026/9/2）
 */
export const formatSlashDate = (date: Date): string => {
  return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
};

/**
 * formatDateOnly
 * @description 日付を年月日の形式にフォーマットする
 * @param dayString 日付の文字列（YYYY-MM-DD）
 * @return string フォーマットされた日付（例: 2024年01月05日）
 */
export const formatDateOnly = (dayString: string): string => {
  return formatDate(dayString, 'YYYY年MM月DD日');
};

/**
 * formatDateWithDay
 * @description 日付を年月日と曜日の形式にフォーマットする
 * @param dayString 日付の文字列（YYYY-MM-DD）
 * @return string フォーマットされた日付（例: 2024年01月05日（金））
 */
export const formatDateWithDay = (dayString: string): string => {
  return formatDate(dayString, 'YYYY年MM月DD日（dd）');
};

/**
 * getYears
 * @description 年数を取得する
 * @return array 年数が格納された配列
 */
export const getYears = (): number[] => {
  const currentYear = new Date().getFullYear();
  return range(SELECTABLE_YEAR_FROM, currentYear);
};

/**
 * getMonths
 * @description 月を取得する
 * @return array 月が格納された配列
 */
export const getMonths = (): number[] => {
  return range(SELECTABLE_MONTH_FROM, SELECTABLE_MONTH_TO);
};

/**
 * getDays
 * @description 日を取得する
 * @return array 日が格納された配列
 */
export const getDays = (): number[] => {
  //暫定的に、すべての月で31日まで表示
  return range(SELECTABLE_DAY_FROM, SELECTABLE_DAY_TO);
};

/**
 * getCurrentMonth
 * @description 現在の月を取得する（JavaScriptの月は0から始まるため、実数値は+1する必要がある）
 * @return number 現在の月
 */
export const getCurrentMonth = (): MonthRange => {
  return new Date().getMonth() + 1;
};

/**
 * getLastDayOfMonth
 * @description 指定された年と月の最後の日を取得する
 * @param year 年
 * @param month 月（1から12の範囲）
 * @return number 最後の日
 */
export const getLastDayOfMonth = (year: number, month: MonthRange): number => {
  return new Date(year, month, 0).getDate();
};

/**
 * その日付が「末日扱い」かどうかを判定
 * @param dateStr YYYY-MM-DD
 * @returns 29日以上 or 月末ならtrue
 */
export const isLastDayOrSpecialEndDay = (dateStr: string): boolean => {
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return false;
  const lastDay = new Date(year, month, 0).getDate();
  return day >= 29 || day === lastDay;
};
/**
 * 指定日付の「○日」または「末日」表記を返す
 * @param dateStr YYYY-MM-DD
 * @returns 1〜28ならそのままの日にち（例: 21日）、29以上なら「末日」
 */
export const getDayOrLastDayLabel = (dateStr: string): string => {
  if (!dateStr) return '';
  const [, , day] = dateStr.split('-').map(Number);
  if (!day) return '';
  return isLastDayOrSpecialEndDay(dateStr) ? '末日' : `${day}日`;
};
/**
 * getNextDeliveryDateWithSpecialMonthEnd
 * @description
 * 指定した日付（YYYY-MM-DD）とサイクル（月数）を元に、次のお届け日を計算します。
 * 1〜28日はそのまま同じ日付、29日以上（29,30,31）は次のcycle月の末日（その月の28,29,30,31日）に設定します。
 * @param baseDateStr 基準日（YYYY-MM-DD）
 * @param cycle 加算する月数（1: 毎月, 2: 隔月, 3: 3ヵ月ごと…。値に上限は無い）
 * @returns ISO形式（YYYY-MM-DD）の日付文字列
 */
export const getNextDeliveryDateWithSpecialMonthEnd = (
  baseDateStr: string,
  cycle: number
): string => {
  const [year, month, day] = baseDateStr.split('-').map(Number);
  if (!year || !month || !day) return '';

  // 1〜28日はそのまま
  if (day >= 1 && day <= 28) {
    const nextDate = new Date(year, month - 1 + cycle, day);
    // タイムゾーンによる日付ずれ防止のため、時刻を昼に固定
    nextDate.setHours(12, 0, 0, 0);

    // ISO形式（YYYY-MM-DD）で返す
    return nextDate.toISOString().slice(0, 10);
  }

  // 29日以上が指定された場合は、次のお届け月の末日
  const nextMonth = month + cycle;
  const lastDay = new Date(year, nextMonth, 0).getDate();
  const nextDate = new Date(year, nextMonth - 1, lastDay);
  // タイムゾーンによる日付ずれ防止のため、時刻を昼に固定
  nextDate.setHours(12, 0, 0, 0);

  // ISO形式（YYYY-MM-DD）で返す
  return nextDate.toISOString().slice(0, 10);
};

/**
 * getNextDeliveryDateByDays
 * @description
 * 指定した日付（YYYY-MM-DD）と日数を元に、次のお届け日を計算します。
 * @param baseDateStr 基準日（YYYY-MM-DD）
 * @param days 加算する日数
 * @returns ISO形式（YYYY-MM-DD）の日付文字列
 */
export const getNextDeliveryDateByDays = (baseDateStr: string, days: number): string => {
  const { $dayjs } = useNuxtApp();
  const baseDate = $dayjs(baseDateStr);
  if (!baseDate.isValid()) return '';
  return baseDate.add(days, 'day').format('YYYY-MM-DD');
};

/**
 * convertJapaneseYearMonthToCardExpiry
 * @description
 * 「2030年01月」から「01/30」のように、和暦年月表記をカード有効期限(MM/YY)形式に変換する
 * @param japaneseDate 例: "2030年01月"
 * @returns MM/YY形式の文字列 例: "01/30"
 * @throws Error フォーマットが不正な場合
 */
export const convertJapaneseYearMonthToCardExpiry = (japaneseDate: string): string => {
  if (!/^\d{4}年\d{2}月$/.test(japaneseDate)) {
    throw createError(`Invalid date format: ${japaneseDate}`);
  }

  const year: string = japaneseDate.slice(2, 4);
  const month: string = japaneseDate.slice(5, 7);
  return `${month}/${year}`;
};
